import { IconPen, IconPlusRounded, IconTrash } from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllCatalogsEth,
  getAllTenantsFilter,
  setModalConfirmationOpen,
  setModalDescriptionOpen,
  setModalFormOpen,
} from "redux/actions";
import styled from "styled-components";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  CatalogEthConfirmationModal,
  CatalogEthDescriptionFormModal,
  CatalogEthFormModal,
} from "./components";
import FilterKit from "components/FilterKit";
import { getRole } from "hooks";
import LazyLoadImageWithBlurHash from "components/LazyLoadImageWithBlurHash";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 20px;
`;

const CatalogsEthContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const {
    appUrl,
    isLoading,
    isModalFormOpen,
    isModalConfirmationOpen,
    isModalDescriptionOpen,
  } = useSelector((state: any) => state.globalReducer);
  const { catalogsEth } = useSelector((state: any) => state.catalogEthReducer);
  const { currentPage, totalPage, limitPage, lengthPage, filterTenantPage } =
    useSelector((state: any) => state.catalogEthReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedCatalogId, setSelectedCatalogId] = useState("");

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllCatalogsEth(
        1,
        event.target.value,
        limitPage,
        filterTenantPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllCatalogsEth(currentPage, "", limitPage, filterTenantPage) as any
    );
  };

  const handleOpenFormModal = (catalogId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedCatalogId(catalogId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedCatalogId("");
  };

  const handleOpenConfirmModal = (catalogId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedCatalogId(catalogId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedCatalogId("");
  };

  const handleOpenCatalogEthDescriptionFormModal = (catalogId: string) => {
    dispatch(setModalDescriptionOpen(true) as any);
    setSelectedCatalogId(catalogId);
  };

  const handleCloseCatalogEthDescriptionFormModal = () => {
    dispatch(setModalDescriptionOpen(false) as any);
    setSelectedCatalogId("");
  };

  useEffect(() => {
    dispatch(getAllTenantsFilter() as any);
    dispatch(getAllCatalogsEth() as any);
  }, [dispatch]);

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Ethereum
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={"space-between"}
          style={{ gap: isMobile ? 10 : 20 }}
        >
          <Box
            display={"flex"}
            flexDirection={isTablet ? "column" : "row"}
            justifyContent={"space-between"}
            style={{ gap: 5 }}
          >
            <InputSearchbox
              autoComplete="off"
              name="textDummy"
              placeholder="Search..."
              size={32}
              onChange={handleSearchChange}
              onClearButton={handleClearSearchChange}
            />
            {userRole !== "tenant" ? (
              <FilterKit
                orderByOptions={[
                  {
                    key: tenantsFilter,
                    label: "Tenant",
                    options: tenantsFilter.map((tenant: any) => {
                      return {
                        label: tenant.name,
                        value: tenant.id,
                      };
                    }),
                  },
                ]}
                onChangeOrder={(e: any) => {
                  dispatch(
                    getAllCatalogsEth(
                      1,
                      "",
                      limitPage,
                      e[tenantsFilter] ? e[tenantsFilter].value : ""
                    ) as any
                  );
                }}
              />
            ) : (
              <></>
            )}
          </Box>

          <Stack
            display={"flex"}
            justifyContent={"right"}
            flexDirection={"row"}
            style={{ gap: isMobile ? 10 : 20 }}
          >
            <Button
              variant="primary"
              borderRadius={10}
              style={{ fontWeight: 600 }}
              icon={IconPlusRounded}
              iconPosition={"left"}
              size={32}
              onClick={() => handleOpenFormModal()}
            >
              Add Ethereum
            </Button>
          </Stack>
        </Box>
      </HeaderWrapper>

      <Card overflow={"auto"} elevation={2} maxHeight={"100%"}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Ethereum</TableHeadCell>
              <TableHeadCell minWidth={150}>Image</TableHeadCell>
              <TableHeadCell>Product</TableHeadCell>
              <TableHeadCell minWidth={150}>Points Needed</TableHeadCell>
              <TableHeadCell>Point Type</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={1} columnsNumber={6} />
            ) : !catalogsEth.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={6} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              catalogsEth.map((catalog: any) => {
                return (
                  <TableBodyRow key={catalog.id}>
                    <TableBodyCell
                      onClick={() =>
                        handleOpenCatalogEthDescriptionFormModal(catalog.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {catalog.catalog_name}
                    </TableBodyCell>
                    <TableBodyCell
                      onClick={() =>
                        handleOpenCatalogEthDescriptionFormModal(catalog.id)
                      }
                      style={{ cursor: "pointer", height: 80 }}
                    >
                      {catalog.img_url ? (
                        <LazyLoadImageWithBlurHash
                          key={catalog.id}
                          src={`${appUrl}/storage/${catalog.img_url}`}
                          blurHash="LEHV6nWB2yk8pyo0adR*.7kCMdnj"
                          alt="catalog-image"
                          height={80}
                          style={{
                            borderRadius: 20,
                          }}
                        />
                      ) : (
                        ""
                      )}
                    </TableBodyCell>
                    <TableBodyCell
                      onClick={() =>
                        handleOpenCatalogEthDescriptionFormModal(catalog.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <Badge size="md" variant="default">
                        {catalog.catalog_product}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell
                      whiteSpace={"nowrap"}
                      onClick={() =>
                        handleOpenCatalogEthDescriptionFormModal(catalog.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <Badge size="md" variant="info">
                        Depending On The Current Rate
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell
                      whiteSpace={"nowrap"}
                      onClick={() =>
                        handleOpenCatalogEthDescriptionFormModal(catalog.id)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <Badge size="md" variant="info">
                        {catalog.point_type_used === "0"
                          ? "XP Point"
                          : catalog.point_type_used === "1"
                          ? "Referral Point"
                          : ""}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <IconButton
                        onClick={() => handleOpenFormModal(catalog.id)}
                      >
                        <IconPen fill={theme.colors.green06} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenConfirmModal(catalog.id)}
                      >
                        <IconTrash fill={theme.colors.red06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: any) =>
            dispatch(
              getAllCatalogsEth(
                select,
                searchValue,
                limitPage,
                filterTenantPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllCatalogsEth(
                  1,
                  searchValue,
                  selectedItem,
                  filterTenantPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <CatalogEthFormModal
        catalogId={selectedCatalogId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />

      <CatalogEthConfirmationModal
        catalogId={selectedCatalogId}
        isOpen={isModalConfirmationOpen}
        onClose={() => handleCloseConfirmModal()}
      />

      <CatalogEthDescriptionFormModal
        catalogId={selectedCatalogId}
        isOpen={isModalDescriptionOpen}
        onClose={() => handleCloseCatalogEthDescriptionFormModal()}
      />
    </Box>
  );
};

export default CatalogsEthContent;
